import './vertrag.css';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../../api';
import { Dialog } from '../../components/Dialog';
import { canShowPdf, PdfPages } from '../../components/Pdf';
import { fmtDate } from '../../lib/format';
import { eurC, parseEuro } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';

// Mandatsvertrag (Sam 13.10.): Bei der Freigabe trägt der Admin die Preise ein, die App füllt den Buchhaltungs- und
// Lohnvertrag aus und legt ihn dem Mandanten zum Unterschreiben in die App (bei Lohn auch die Vollmachten). Für schon
// freigegebene Mandanten derselbe Weg in den Stammdaten. Die Angaben des Büros dafür stehen unter „Abrechnung“.

export interface Preise {
  fibu: boolean;
  lohn: boolean;
  feeFibu: string;
  feeAbrechnung: string;
  feeAnmeldung: string;
  feeEinrichtung: string;
  feeJa: string;
}
export interface PreiseBody {
  fibu: boolean;
  lohn: boolean;
  feeFibu: number | null;
  feeAbrechnung: number | null;
  feeAnmeldung: number | null;
  feeEinrichtung: number | null;
  feeJa: number | null;
}
interface DocStand {
  id: string;
  signedAt: string | null;
  removed: boolean;
}
export interface VertragStand {
  fehlt: string | null;
  vertrag: {
    createdAt: string;
    fibu: boolean;
    lohn: boolean;
    feeFibu: number | null;
    feeAbrechnung: number | null;
    feeAnmeldung: number | null;
    feeEinrichtung: number;
    feeJa: number | null;
    vertrag: DocStand | null;
    vollmacht: DocStand | null;
  } | null;
}

export const LEER: Preise = { fibu: true, lohn: false, feeFibu: '', feeAbrechnung: '', feeAnmeldung: '', feeEinrichtung: '', feeJa: '' };
const zahl = (v: number | null) => (v == null ? '' : v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export function preiseAus(v: VertragStand['vertrag']): Preise {
  if (!v) return LEER;
  return {
    fibu: v.fibu,
    lohn: v.lohn,
    feeFibu: zahl(v.feeFibu),
    feeAbrechnung: zahl(v.feeAbrechnung),
    feeAnmeldung: zahl(v.feeAnmeldung),
    feeEinrichtung: v.feeEinrichtung ? zahl(v.feeEinrichtung) : '',
    feeJa: zahl(v.feeJa),
  };
}

// Eingaben prüfen: Body für den Server oder die Meldung, was fehlt. Kein Haken = kein Vertrag (null).
export function preiseBody(p: Preise): { body: PreiseBody | null } | { error: string } {
  if (!p.fibu && !p.lohn) return { body: null };
  const read = (text: string, need: boolean, msg: string): number | null | string => {
    const v = parseEuro(text);
    if (v === null) return need ? msg : null;
    if (v === undefined || v < 0 || v >= 1e6) return msg;
    return v;
  };
  const fibu = p.fibu ? read(p.feeFibu, true, 'Bitte den Monatspreis für die Buchhaltung eintragen, z. B. 150,00.') : null;
  const abr = p.lohn ? read(p.feeAbrechnung, true, 'Bitte den Preis je Verdienstabrechnung eintragen, z. B. 25,00.') : null;
  const anm = p.lohn ? read(p.feeAnmeldung, true, 'Bitte den Preis je Anmeldung eines neuen Mitarbeiters eintragen, z. B. 30,00.') : null;
  const ein = read(p.feeEinrichtung, false, 'Bitte die Einrichtungsgebühr prüfen, z. B. 250,00, oder leer lassen.');
  const ja = p.fibu ? read(p.feeJa, false, 'Bitte den Preis für die Vorbereitung des Jahresabschlusses prüfen, z. B. 400,00, oder leer lassen.') : null;
  for (const x of [fibu, abr, anm, ein, ja]) if (typeof x === 'string') return { error: x };
  return {
    body: {
      fibu: p.fibu,
      lohn: p.lohn,
      feeFibu: fibu as number | null,
      feeAbrechnung: abr as number | null,
      feeAnmeldung: anm as number | null,
      feeEinrichtung: ein as number | null,
      feeJa: ja as number | null,
    },
  };
}

// Haken für Buchhaltung und Lohn mit den vier Preisen
export function PreisFelder({ value, onChange, idPrefix }: { value: Preise; onChange(p: Preise): void; idPrefix: string }) {
  const set = (v: Partial<Preise>) => onChange({ ...value, ...v });
  const money = (key: 'feeFibu' | 'feeAbrechnung' | 'feeAnmeldung' | 'feeEinrichtung' | 'feeJa', label: string, hint?: string) => (
    <label htmlFor={`${idPrefix}-${key}`}>
      {label}
      <input
        id={`${idPrefix}-${key}`}
        className="num"
        value={value[key]}
        inputMode="decimal"
        placeholder={hint}
        onChange={(e) => set({ [key]: e.target.value })}
      />
    </label>
  );
  return (
    <div className="vt-felder">
      <label className="vt-modul">
        <input type="checkbox" checked={value.fibu} onChange={(e) => set({ fibu: e.target.checked })} />
        <b>Buchhaltung</b>
      </label>
      {value.fibu && (
        <div className="vt-preise vt-two">
          {money('feeFibu', 'Monatlich (€ netto) *', 'z. B. 150,00')}
          {money('feeJa', 'Vorbereitung Jahresabschluss je Jahr (€ netto)', 'leer = nicht gebucht')}
        </div>
      )}
      <label className="vt-modul">
        <input type="checkbox" checked={value.lohn} onChange={(e) => set({ lohn: e.target.checked })} />
        <b>Lohn</b>
      </label>
      {value.lohn && (
        <div className="vt-preise vt-two">
          {money('feeAbrechnung', 'Je Verdienstabrechnung (€ netto) *', 'z. B. 25,00')}
          {money('feeAnmeldung', 'Je Anmeldung neuer Mitarbeiter (€ netto) *', 'z. B. 30,00')}
        </div>
      )}
      {(value.fibu || value.lohn) && (
        <div className="vt-preise">{money('feeEinrichtung', 'Einrichtungsgebühr einmalig (€ netto)', 'leer = keine')}</div>
      )}
    </div>
  );
}

// Bei einer Registrierung: was im Freigabe-Formular steht (Nummer, Voranmeldung, Zuständige), damit die Vorschau dem
// Vertrag entspricht, den die Freigabe erstellt
export interface Entwurf {
  number: string;
  ustva: string;
  fibuUserId: string | null;
  lohnUserId: string | null;
}

// PDF-Vorschau von Vertrag und Vollmachten (wird nicht gespeichert)
export function Vorschau({ clientId, body, onClose }: { clientId: string; body: PreiseBody & Partial<Entwurf>; onClose(): void }) {
  const [doc, setDoc] = useState<'vertrag' | 'vollmacht'>('vertrag');
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let gone = false;
    let made: string | null = null;
    setUrl(null);
    setError(null);
    api
      .blob(`/api/kanzlei/clients/${clientId}/vertrag/vorschau`, { ...body, doc })
      .then((b) => {
        made = URL.createObjectURL(b);
        if (gone) URL.revokeObjectURL(made);
        else setUrl(made);
      })
      .catch((err: unknown) => {
        if (!gone) setError(errorText(err));
      });
    return () => {
      gone = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [clientId, body, doc]);
  const title = doc === 'vertrag' ? 'Vorschau des Vertrags' : 'Vorschau der Vollmachten';
  return (
    <Dialog open onClose={onClose} title="Vorschau Mandatsvertrag" className="vtdlg">
      {body.lohn && (
        <div className="seg vt-seg">
          <button type="button" aria-pressed={doc === 'vertrag'} onClick={() => setDoc('vertrag')}>
            Vertrag
          </button>
          <button type="button" aria-pressed={doc === 'vollmacht'} onClick={() => setDoc('vollmacht')}>
            Vollmachten
          </button>
        </div>
      )}
      <div className="vt-prev">
        {error ? (
          <p className="note">{error}</p>
        ) : !url ? (
          <div className="spinner" aria-label="Lädt" />
        ) : canShowPdf() ? (
          <iframe src={url} title={title} />
        ) : (
          <PdfPages url={url} title={title} fallback={<p className="note">Die Vorschau kann hier nicht angezeigt werden.</p>} />
        )}
      </div>
      {url && (
        <p className="note vt-open">
          <a href={url} target="_blank" rel="noopener noreferrer">
            In neuem Fenster öffnen
          </a>
        </p>
      )}
      <div className="btnrow">
        <button type="button" className="btn" onClick={onClose}>
          Schließen
        </button>
      </div>
    </Dialog>
  );
}

// Fehlen Angaben des Büros, kann die App keinen Vertrag ausfüllen
export function FehltHinweis({ text }: { text: string }) {
  return (
    <div className="lock">
      {text} <Link to="/kanzlei/abrechnung">Zur Abrechnung</Link>
    </div>
  );
}

// Stammdaten › Mandatsvertrag (Admin, freigegebene Mandanten): Stand und „Vertrag erstellen“
export function Mandatsvertrag({ clientId, clientName }: { clientId: string; clientName: string }) {
  const toast = useToast();
  const s = useApi<VertragStand>(`/api/kanzlei/clients/${clientId}/vertrag`, { live: ['docs', 'abrechnung'], clientId });
  const [form, setForm] = useState<Preise | null>(null);
  const [preview, setPreview] = useState<PreiseBody | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const v = s.data?.vertrag ?? null;

  const check = (): PreiseBody | null => {
    const r = preiseBody(form!);
    if ('error' in r) {
      setError(r.error);
      return null;
    }
    if (!r.body) {
      setError('Bitte Buchhaltung, Lohn oder beides ankreuzen.');
      return null;
    }
    return r.body;
  };
  const create = async (e: FormEvent) => {
    e.preventDefault();
    const body = check();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/clients/${clientId}/vertrag`, body);
      toast(`Vertrag erstellt · ${clientName} kann ihn jetzt in der App unterschreiben`);
      setForm(null);
      setError(null);
      await s.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  // Beim neuesten Vertrag heißt „entfernt“: in den Dokumenten gelöscht (ein neuer Vertrag ersetzt nur ältere)
  const stand = (d: DocStand | null, name: string) =>
    !d ? null : d.removed ? (
      <span className="pill">{name} entfernt, bitte neu erstellen</span>
    ) : d.signedAt ? (
      <span className="pill p-ok">
        {name} unterschrieben am {fmtDate(d.signedAt)}
      </span>
    ) : (
      <span className="pill p-warn">{name}: wartet auf Unterschrift</span>
    );

  return (
    <section className="card vt" aria-labelledby="vt-h">
      <div className="ch">
        <h3 id="vt-h">Mandatsvertrag</h3>
        <span>Buchhaltungs- und Lohnvertrag</span>
      </div>
      {!s.data ? (
        s.error ? <p className="note">{s.error}</p> : <div className="spinner" aria-label="Lädt" />
      ) : (
        <>
          {v ? (
            <div className="vt-stand">
              <p>
                Erstellt am {fmtDate(v.createdAt)} · {[v.fibu && 'Buchhaltung', v.lohn && 'Lohn'].filter(Boolean).join(' und ')}
              </p>
              <p className="vt-pills">
                {stand(v.vertrag, 'Vertrag')}
                {stand(v.vollmacht, 'Vollmachten')}
              </p>
              <p className="note">
                {[
                  v.fibu && v.feeFibu != null && `Buchhaltung ${eurC(v.feeFibu)} im Monat`,
                  v.fibu && v.feeJa != null && `Vorbereitung Jahresabschluss ${eurC(v.feeJa)} im Jahr`,
                  v.lohn && v.feeAbrechnung != null && `${eurC(v.feeAbrechnung)} je Verdienstabrechnung`,
                  v.lohn && v.feeAnmeldung != null && `${eurC(v.feeAnmeldung)} je Anmeldung`,
                  v.feeEinrichtung > 0 && `Einrichtung ${eurC(v.feeEinrichtung)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          ) : (
            <p className="note">Noch kein Vertrag aus der App. Die App füllt ihn mit den Angaben des Mandanten und Ihren Preisen aus.</p>
          )}
          {s.data.fehlt ? (
            <FehltHinweis text={s.data.fehlt} />
          ) : form ? (
            <form className="form" onSubmit={create} noValidate>
              <PreisFelder
                idPrefix="vt"
                value={form}
                onChange={(p) => {
                  setForm(p);
                  setError(null);
                }}
              />
              {v?.vertrag?.signedAt && (
                <p className="note">Der unterschriebene Vertrag bleibt in der App. Der neue gilt, sobald {clientName} ihn unterschreibt.</p>
              )}
              {v?.vertrag && !v.vertrag.signedAt && !v.vertrag.removed && (
                <p className="note">Der noch nicht unterschriebene Vertrag wird durch den neuen ersetzt.</p>
              )}
              {error && (
                <p className="note vt-err" role="alert">
                  {error}
                </p>
              )}
              <div className="btnrow">
                <button type="submit" className="btn dark" disabled={busy}>
                  Vertrag erstellen
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => {
                    const b = check();
                    if (b) setPreview(b);
                  }}
                >
                  Vorschau
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setForm(null);
                    setError(null);
                  }}
                  disabled={busy}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          ) : (
            <div className="btnrow">
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  setForm(preiseAus(v));
                  setError(null);
                }}
              >
                {v ? 'Neuen Vertrag erstellen' : 'Vertrag erstellen'}
              </button>
            </div>
          )}
        </>
      )}
      {preview && <Vorschau clientId={clientId} body={preview} onClose={() => setPreview(null)} />}
    </section>
  );
}

// Abrechnung › Angaben für den Mandatsvertrag (einmal für alle Verträge)
interface Settings {
  vertreterName: string;
  vertreterFunktion: string;
  verantwortlich: string;
  register: string | null;
  versicherung: number;
  stundensatz: number;
  massnahmenBis: string;
  dsb: string | null;
  lohnprogramm: string | null;
  lohnprogrammHersteller: string | null;
  betriebsnummer: string | null;
}
type Form = Record<keyof Settings, string>;
// Vorschlag für offene Sicherheits-Punkte: Jahresende, ab November das nächste
const jahresende = () => {
  const d = new Date();
  return `${d.getFullYear() + (d.getMonth() >= 10 ? 1 : 0)}-12-31`;
};
const formOf = (s: Settings | null): Form => ({
  vertreterName: s?.vertreterName ?? '',
  vertreterFunktion: s?.vertreterFunktion ?? '',
  verantwortlich: s?.verantwortlich ?? '',
  register: s?.register ?? '',
  versicherung: zahl(s?.versicherung ?? 1000000).replace(/,00$/, ''),
  stundensatz: zahl(s?.stundensatz ?? 90),
  massnahmenBis: s?.massnahmenBis ?? jahresende(),
  dsb: s?.dsb ?? '',
  lohnprogramm: s?.lohnprogramm ?? '',
  lohnprogrammHersteller: s?.lohnprogrammHersteller ?? '',
  betriebsnummer: s?.betriebsnummer ?? '',
});

export function VertragAngaben() {
  const toast = useToast();
  const res = useApi<{ settings: Settings | null }>('/api/kanzlei/vertrag/settings', { live: ['abrechnung'] });
  const [draft, setDraft] = useState<Partial<Form>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [more, setMore] = useState(false);
  if (!res.data) return null;
  const saved = res.data.settings;
  const f: Form = { ...formOf(saved), ...draft };
  const set = (v: Partial<Form>) => {
    setDraft((x) => ({ ...x, ...v }));
    setError(null);
  };
  const field = (key: keyof Form, label: string, extra: { max?: number; hint?: string; mode?: 'numeric' | 'decimal'; type?: string } = {}) => (
    <label>
      {label}
      <input
        value={f[key]}
        type={extra.type ?? 'text'}
        maxLength={extra.max ?? 120}
        inputMode={extra.mode}
        placeholder={extra.hint}
        onChange={(e) => set({ [key]: e.target.value })}
      />
    </label>
  );

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const vers = parseEuro(f.versicherung);
    const std = parseEuro(f.stundensatz);
    if (!vers || vers <= 0) return setError('Bitte die Versicherungssumme prüfen, z. B. 1.000.000.');
    if (!std || std <= 0) return setError('Bitte den Stundensatz prüfen, z. B. 90,00.');
    setError(null);
    setBusy(true);
    try {
      await api.put('/api/kanzlei/vertrag/settings', {
        vertreterName: f.vertreterName,
        vertreterFunktion: f.vertreterFunktion,
        verantwortlich: f.verantwortlich,
        register: f.register,
        versicherung: vers,
        stundensatz: std,
        massnahmenBis: f.massnahmenBis,
        dsb: f.dsb,
        lohnprogramm: f.lohnprogramm,
        lohnprogrammHersteller: f.lohnprogrammHersteller,
        betriebsnummer: f.betriebsnummer,
      });
      setDraft({});
      await res.reload();
      toast('Angaben für den Mandatsvertrag gespeichert');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card vt" aria-labelledby="vt-set-h">
      <div className="ch">
        <h3 id="vt-set-h">Angaben für den Mandatsvertrag</h3>
        <span>stehen in jedem Vertrag aus der App</span>
      </div>
      {!saved && (
        <p className="note">
          Einmal eintragen, dann füllt die App bei jeder Freigabe den Buchhaltungs- und Lohnvertrag aus. Name, Anschrift und
          E-Mail des Büros kommen aus „Angaben auf der Rechnung“.
        </p>
      )}
      <form className="form" onSubmit={save} noValidate>
        <div className="row vt-two">
          {field('vertreterName', 'Unterschreibt für das Büro (Name) *')}
          {field('vertreterFunktion', 'Funktion *', { hint: 'z. B. Inhaber oder Geschäftsführer' })}
        </div>
        {field('verantwortlich', 'Verantwortlich für Buchhaltung und Lohn (Name) *', { hint: 'die Person mit der Erlaubnis dafür' })}
        {field('register', 'Handelsregister', { hint: 'z. B. Amtsgericht Hamburg, HRB 12345 (leer, wenn keins)' })}
        <div className="row vt-two">
          {field('versicherung', 'Versicherungssumme je Schadensfall (€) *', { mode: 'decimal' })}
          {field('stundensatz', 'Stundensatz für Extras (€ netto) *', { mode: 'decimal' })}
        </div>
        <label>
          Offene Sicherheits-Punkte erledigt bis *
          <input type="date" value={f.massnahmenBis} onChange={(e) => set({ massnahmenBis: e.target.value })} />
          <span className="note">Dieses Datum steht im Vertrag bei den technischen Maßnahmen, die noch geplant sind.</span>
        </label>
        {more ? (
          <>
            {field('dsb', 'Datenschutzbeauftragter (Name, Anschrift, E-Mail)', { max: 200, hint: 'leer, wenn keiner benannt ist' })}
            <div className="row vt-two">
              {field('lohnprogramm', 'Lohnprogramm', { hint: 'z. B. DATEV LODAS' })}
              {field('betriebsnummer', 'Betriebsnummer des Büros', { max: 8, mode: 'numeric' })}
            </div>
            {field('lohnprogrammHersteller', 'Hersteller mit Anschrift', {
              max: 200,
              hint: 'nur, wenn das Programm im Rechenzentrum des Herstellers in Deutschland läuft',
            })}
          </>
        ) : (
          <div>
            <button type="button" className="btn sm" onClick={() => setMore(true)}>
              Weitere Angaben (freiwillig)
            </button>
          </div>
        )}
        {error && (
          <p className="note vt-err" role="alert">
            {error}
          </p>
        )}
        <div>
          <button type="submit" className="btn dark" disabled={busy}>
            Speichern
          </button>
        </div>
      </form>
    </section>
  );
}
